import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "preview", "dropZone", "loading"]

  static values = {
    maxFiles: { type: Number, default: 5 },
    maxSize: { type: Number, default: 5242880 }, // 5MB
    removeFieldName: { type: String, default: "campaign[remove_image_ids][]" },
    msgMaxFilesExceeded: { type: String, default: "최대 %{max}장까지 업로드 가능합니다." },
    msgFileTooLarge: { type: String, default: "파일 크기는 5MB 이하여야 합니다." },
    msgNotImage: { type: String, default: "이미지 파일만 업로드 가능합니다." },
    msgBadgeRepresentative: { type: String, default: "대표" }
  }

  _interpolate(template, vars) {
    return template.replace(/%\{(\w+)\}/g, (_, k) => vars[k] !== undefined ? vars[k] : "")
  }

  connect() {
    this.files = []      // 새로 추가된 파일 목록 (DataTransfer 동기화용)
    this._draggedItem = null
    this.bindDropEvents()
    // 수정 폼 등 서버에서 렌더링된 기존 이미지에 배지 + 드래그 초기화
    this.initExistingItems()
  }

  // ───── 업로드 드롭존 이벤트 (파일 드래그 업로드) ─────
  bindDropEvents() {
    if (!this.hasDropZoneTarget) return

    this.dropZoneTarget.addEventListener("dragover", (e) => {
      e.preventDefault()
      this.dropZoneTarget.style.borderColor = "#4F46E5"
      this.dropZoneTarget.style.background = "#EEF2FF"
    })

    this.dropZoneTarget.addEventListener("dragleave", (e) => {
      e.preventDefault()
      this.dropZoneTarget.style.borderColor = "#E5E7EB"
      this.dropZoneTarget.style.background = "#F9FAFB"
    })

    this.dropZoneTarget.addEventListener("drop", (e) => {
      e.preventDefault()
      this.dropZoneTarget.style.borderColor = "#E5E7EB"
      this.dropZoneTarget.style.background = "#F9FAFB"
      this.handleFiles(e.dataTransfer.files)
    })
  }

  triggerInput() {
    if (this.hasInputTarget) this.inputTarget.click()
  }

  onChange() {
    if (this.hasInputTarget && this.inputTarget.files.length > 0) {
      this.handleFiles(this.inputTarget.files)
    }
  }

  handleFiles(files) {
    const existingCount = this.previewTarget.querySelectorAll(".image-preview-item").length
    const available = this.maxFilesValue - existingCount

    if (available <= 0) {
      alert(this._interpolate(this.msgMaxFilesExceededValue, { max: this.maxFilesValue }))
      return
    }

    this.showLoading()

    const validFiles = []
    for (let i = 0; i < files.length; i++) {
      if (validFiles.length >= available) {
        alert(this._interpolate(this.msgMaxFilesExceededValue, { max: this.maxFilesValue }))
        break
      }
      const file = files[i]
      if (file.size > this.maxSizeValue) { alert(`${file.name}: ${this.msgFileTooLargeValue}`); continue }
      if (!file.type.startsWith("image/")) { alert(`${file.name}: ${this.msgNotImageValue}`); continue }
      validFiles.push(file)
    }

    if (validFiles.length === 0) { this.hideLoading(); return }

    let processedCount = 0
    validFiles.forEach((file, i) => {
      this.files.push(file)
      setTimeout(() => {
        this.createPreview(file)
        this.syncInput()
        processedCount++
        if (processedCount === validFiles.length) this.hideLoading()
      }, i * 200)
    })
  }

  showLoading() { if (this.hasLoadingTarget) this.loadingTarget.classList.add('show') }
  hideLoading() { if (this.hasLoadingTarget) this.loadingTarget.classList.remove('show') }

  // ───── 파일 목록 → file input 동기화 ─────
  syncInput() {
    if (!this.hasInputTarget) return
    const dt = new DataTransfer()
    this.files.forEach(f => dt.items.add(f))
    this.inputTarget.files = dt.files
  }

  // 드래그 후 DOM 순서에서 files 배열 재구성
  syncFilesFromDom() {
    const items = this.previewTarget.querySelectorAll('.image-preview-item')
    this.files = Array.from(items).map(item => item._file).filter(Boolean)
    this.syncInput()
  }

  // ───── 대표 배지 관리 ─────
  updateBadges() {
    const items = this.previewTarget.querySelectorAll('.image-preview-item')
    items.forEach((item, idx) => {
      let badge = item.querySelector('.rep-badge')
      if (!badge) {
        badge = this._createBadgeEl()
        item.appendChild(badge)
      }
      badge.style.display = idx === 0 ? 'block' : 'none'
    })
  }

  _createBadgeEl() {
    const badge = document.createElement("span")
    badge.className = "rep-badge"
    badge.textContent = this.msgBadgeRepresentativeValue
    badge.style.cssText = [
      "position: absolute",
      "bottom: 0",
      "left: 0",
      "right: 0",
      "background: rgba(79,70,229,0.88)",
      "color: #fff",
      "font-size: 11px",
      "font-weight: 700",
      "text-align: center",
      "padding: 3px 0",
      "pointer-events: none",
      "letter-spacing: 0.5px",
      "display: none"
    ].join(";")
    return badge
  }

  // ───── 드래그 앤 드롭 (이미지 순서 변경) ─────
  addDragHandlers(container) {
    container.draggable = true
    container.style.cursor = "grab"

    container.addEventListener("dragstart", (ev) => {
      this._draggedItem = container
      ev.dataTransfer.effectAllowed = "move"
      // 살짝 딜레이를 주어야 Firefox에서도 안정적
      setTimeout(() => { container.style.opacity = "0.45" }, 0)
    })

    container.addEventListener("dragend", () => {
      container.style.opacity = "1"
      this._draggedItem = null
      this.previewTarget.querySelectorAll('.image-preview-item').forEach(el => {
        el.style.outline = ""
        el.style.transform = ""
      })
    })

    container.addEventListener("dragover", (ev) => {
      ev.preventDefault()
      ev.dataTransfer.dropEffect = "move"
      if (this._draggedItem && this._draggedItem !== container) {
        container.style.outline = "2px solid #4F46E5"
      }
    })

    container.addEventListener("dragleave", () => {
      container.style.outline = ""
    })

    container.addEventListener("drop", (ev) => {
      ev.preventDefault()
      ev.stopPropagation()
      container.style.outline = ""
      if (!this._draggedItem || this._draggedItem === container) return

      const allItems = Array.from(this.previewTarget.querySelectorAll('.image-preview-item'))
      const draggedIdx = allItems.indexOf(this._draggedItem)
      const targetIdx = allItems.indexOf(container)

      if (draggedIdx < targetIdx) {
        container.after(this._draggedItem)
      } else {
        container.before(this._draggedItem)
      }

      this.syncFilesFromDom()
      this.updateBadges()
    })
  }

  // ───── 서버 렌더링된 기존 이미지 초기화 ─────
  initExistingItems() {
    const existingItems = this.previewTarget.querySelectorAll('.image-preview-item')
    existingItems.forEach(item => {
      // X 버튼 (서버 이미지 삭제)
      const blobId = item.dataset.blobId
      if (blobId) {
        const removeBtn = document.createElement("button")
        removeBtn.type = "button"
        removeBtn.innerHTML = "&times;"
        removeBtn.style.cssText = "position: absolute; top: 2px; right: 2px; width: 22px; height: 22px; background: rgba(0,0,0,0.6); color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; line-height: 1; display: flex; align-items: center; justify-content: center; z-index: 1;"
        removeBtn.addEventListener("click", (ev) => {
          ev.stopPropagation()
          // 삭제 대상 blob id를 hidden input으로 폼에 추가
          const hiddenInput = document.createElement("input")
          hiddenInput.type = "hidden"
          hiddenInput.name = this.removeFieldNameValue
          hiddenInput.value = blobId
          this.element.closest("form").appendChild(hiddenInput)
          item.remove()
          this.updateBadges()
        })
        item.appendChild(removeBtn)
      }
      // 커서 스타일 + 드래그 핸들러
      this.addDragHandlers(item)
    })
    this.updateBadges()
  }

  // ───── 이미지 미리보기 생성 ─────
  createPreview(file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      const container = document.createElement("div")
      container.className = "image-preview-item"
      container.style.cssText = "position: relative; display: inline-block; width: 100px; height: 100px; margin: 4px;"
      container._file = file   // 드래그 후 files 배열 재구성에 사용

      // 이미지
      const img = document.createElement("img")
      img.src = e.target.result
      img.style.cssText = "width: 100%; height: 100%; object-fit: cover; border-radius: 0; pointer-events: none;"

      // 대표 배지
      const badge = this._createBadgeEl()

      // X 버튼
      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.innerHTML = "&times;"
      removeBtn.style.cssText = "position: absolute; top: 2px; right: 2px; width: 22px; height: 22px; background: rgba(0,0,0,0.6); color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; line-height: 1; display: flex; align-items: center; justify-content: center; z-index: 1;"
      removeBtn.addEventListener("click", (ev) => {
        ev.stopPropagation()
        this.files = this.files.filter(f => f !== file)
        this.syncInput()
        container.remove()
        this.updateBadges()
      })

      container.appendChild(img)
      container.appendChild(badge)
      container.appendChild(removeBtn)

      // 드래그 핸들러 추가 후 DOM에 삽입
      this.addDragHandlers(container)
      this.previewTarget.appendChild(container)

      // 첫 번째 이미지면 즉시 배지 표시, 아니면 updateBadges가 처리
      this.updateBadges()
    }
    reader.readAsDataURL(file)
  }

}
